/* eslint-disable react-hooks/exhaustive-deps */
import StatusReportChart, { WorkstreamHealth } from "@/Components/StatusReport/StatusReportChart"
import TrafficLightComponent from "@/Components/TrafficLightComponent"
import { Card } from "@/Components/ui/card"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/Components/ui/table"
import { Check, FileStack, FileSymlink } from "lucide-react"
import React from "react"
import { format, formatDate } from "date-fns"
import { dateformat } from "@/utils/GlobalConstants"
import { AppContext, WorkPackageOptions } from "../App"
import { Textarea } from "../Components/ui/textarea"
import { Button } from "../Components/ui/button"
import { DropdownMenu, DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu"
import { DropdownMenuContent, DropdownMenuItem } from "@/Components/ui/dropdown-menu"
import Loading from "@/Components/loading"
import { useLocation, useNavigate } from "react-router-dom"
import { DeliverableIcon, MilestoneIcon, TaskIcon, UndoIcon } from "@/Components/ui/Icons/TooltipIcon"
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog"
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider"
import { getBasicTourSteps } from "@/Components/StatusReport/status_report_wizards/basic_page_tour"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip"
import HelpLink from "@/Components/ui/help-link"
import { StatusReportContext } from "@/Components/StatusReport/StatusReportContext"
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom"
import { useIsMobile } from "@/hooks/use-mobile";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker"
import { Tutorial } from "@/lib/utils"

export const useDate = () => {
    const locale = 'en';
    const [today, setDate] = React.useState(new Date()); // Save the current date to be able to trigger an update

    React.useEffect(() => {
        const timer = setInterval(() => { // Creates an interval which will update the current data every minute
            // This will trigger a rerender every component that uses the useDate hook.
            setDate(new Date());
        }, 60 * 1000);
        return () => {
            clearInterval(timer); // Return a funtion to clear the timer so that it will stop being called on unmount
        }
    }, []);

    const day = today.toLocaleDateString(locale, { weekday: 'long' });
    const date = `${day}, ${format(today, dateformat)}\n\n`;

    const time = today.toLocaleTimeString(locale, { hour: 'numeric', hour12: true, minute: 'numeric' });

    return {
        date,
        time,
    };
};

export type TaskBasic = {
    taskId: number
    name: string
    status: 2 | 1 | 4
    finishDate: Date
    completed: boolean
    completedDate: Date
    isMilestone: boolean
    isDeliverable: boolean
    displayOnCharter: boolean
    displayOnStatusReport: boolean
}

export type RiskIssueBasic = {
    id: number
    description: string
    mitigation: string
    rating: "High" | "Medium" | "Low"
    isRisk: boolean
}

export type WorkPackageBasic = {
    workPackageId: number
    name: string
    startDate: Date
    finishDate: Date
    tasks: TaskBasic[]
}

export type SupportRequestBasic = {
    supportRequestId: number
    description: string
    date: Date
}

export type WorkstreamOption = {
    workstreamId: number
    workstreamName: string
}

export type StatusReport = {
    statusReportId: number
    workstreamId: number
    workstreamName: string
    date: Date
    estimatedCloseDate: Date
    health: WorkstreamHealth
    activitiesSince?: string
    nextSteps?: string
    workPackages: WorkPackageBasic[]
    riskIssues: RiskIssueBasic[]
    supportRequests: SupportRequestBasic[]
}

function StatusReportPage() {
    const app = React.useContext(AppContext);
    const isMobile = useIsMobile();
    const lock = React.useContext(PageLockContext);
    const wizard = React.useContext(WizardContext);
    const zoomLevel = useResponsiveZoom();
    const [demoMode, setDemoMode] = React.useState<boolean>(false);

    const location = useLocation();
    const locationState = location.state as { workstream: string } | null;
    const passedWorkstream = locationState?.workstream || null;

    const [isLoading, setIsLoading] = React.useState(true);
    const { date, time } = useDate();

    const navigate = useNavigate();

    const [nextStepsCount, setNextStepsCount] = React.useState(0);
    const [activitiessCount, setActivitiesCount] = React.useState(0);
    const [selectedWorkstream, setSelectedWorkstream] = React.useState<WorkstreamOption>();
    const [workstreamStatusReport, setWorkstreamStatusReport] = React.useState<StatusReport | undefined>(undefined);
    const [demoStatusReport, setDemoStatusReport] = React.useState<StatusReport | undefined>(undefined);

    const [FormMessage, setFormMessage] = React.useState<JSX.Element>();

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState("Report Created");
    const [dialogContent, setDialogContent] = React.useState<JSX.Element | null>(null);

    React.useEffect(() => {
        const storedWorkstream = loadSelectedWorkstream();
        if (storedWorkstream) {
            setSelectedWorkstream(storedWorkstream);
        }
        else if (app.workPackageOptions && app.workPackageOptions.length > 0) {
            setSelectedWorkstream({ workstreamId: app.workPackageOptions[0].workstreamId, workstreamName: app.workPackageOptions[0].workstreamName });
        }
    }, [app.workPackageOptions]);

    React.useEffect(() => {
        if (passedWorkstream) {
            if (passedWorkstream === "Project" && app.selectedProject?.projectTypeId === 2) {
                setSelectedWorkstream({ workstreamId: 0, workstreamName: "Project" });
                saveSelectedWorkstream({ workstreamId: 0, workstreamName: "Project" });
            } else {
                const workstream = app.workPackageOptions?.find((ws) => ws.workstreamName === passedWorkstream);
                if (workstream) {
                    saveSelectedWorkstream({ workstreamId: workstream.workstreamId, workstreamName: workstream.workstreamName });
                }
            }
        }
    }, [passedWorkstream, app.workPackageOptions]);

    // Fetch the status report data when the selected workstream changes
    // This will be called when the component mounts and when the selected workstream changes

    React.useEffect(() => {
        if (!selectedWorkstream) return;
        loadStatusReport();
    }, [selectedWorkstream]);

    React.useEffect(() => {
        if (!workstreamStatusReport) return;
        setFormMessage(getFormMessage()); // Update the form message when the workstreamStatusReport changes
    }, [workstreamStatusReport]);

    // Populate demo data on mount
    React.useEffect(() => {
        populateDemoData();
    }, []);

    // Use demo data when in demo mode, otherwise use real data
    const displayReport = demoMode ? demoStatusReport : workstreamStatusReport;

    React.useEffect(() => {
        setActivitiesCount(displayReport?.activitiesSince?.length ?? 0);
        setNextStepsCount(displayReport?.nextSteps?.length ?? 0);
    }, [displayReport]);

    function UpdateReport() {
        if (demoMode) {
            showReportCreatedDialog(true);
            return;
        }
        if (!workstreamStatusReport) return;
        const updatedReport = {
            ...workstreamStatusReport,
            activitiesSince: workstreamStatusReport.activitiesSince,
            nextSteps: workstreamStatusReport.nextSteps,
        };
        document.body.style.cursor = 'wait';
        fetch(`Reports/StatusReport/${workstreamStatusReport.statusReportId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedReport),
        })
            .then((response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Status report updated successfully", "success");
                    localStorage.removeItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId);
                    setWorkstreamStatusReport({
                        ...workstreamStatusReport,
                        date: new Date(),
                    });
                } else {
                    app.showActionNotification("Error", "Error updating status report", "destructive");
                }
            })
            .catch(() => {
                app.showActionNotification("Error", "Error updating status report", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
                showReportCreatedDialog(true);
            });
    }

    function CreateReport() {
        if (demoMode) {
            showReportCreatedDialog();
            return;
        }
        if (!workstreamStatusReport) return;
        const newReport = {
            ...workstreamStatusReport,
            activitiesSince: workstreamStatusReport.activitiesSince,
            nextSteps: workstreamStatusReport.nextSteps,
        };
        document.body.style.cursor = 'wait';
        fetch(`Reports/StatusReport/${selectedWorkstream?.workstreamId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(newReport),
        })
            .then(async (response) => {
                if (response.ok) {
                    app.showActionNotification("Success", "Status report created successfully", "success");
                    return response.json(); // Parse and return JSON data
                } else {
                    app.showActionNotification("Error", "Error creating status report", "destructive");
                    throw new Error("Failed to create status report");
                }
            })
            .then((data) => {
                if (data.statusReportId) {
                    setWorkstreamStatusReport({
                        ...workstreamStatusReport,
                        statusReportId: data.statusReportId,
                        date: new Date(),
                    });
                    localStorage.removeItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId); // Clear local storage after creating a new report

                } else {
                    app.showActionNotification("Error", "Invalid data or missing statusReportId", "destructive");
                }
            })
            .catch(() => {
                app.showActionNotification("Error", "Error creating status report", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
                showReportCreatedDialog();
            });
    }

    function showReportCreatedDialog(isUpdate: boolean = false) {
        setDialogTitle(demoMode ? "Report Created (Demo Mode)" : "Report Created");
        setDialogContent(
            <div>
                <p>Your report has been {isUpdate ? "updated" : "created"} successfully.</p>
                <div className="flex flex-row">
                    <Button className="mt-4 mx-auto button-default" onClick={() => navigate("/Reports")}>View Report</Button>
                </div>
            </div>
        );
        setDialogOpen(true);
    }

    function loadStatusReport() {
        if (!selectedWorkstream) return;

        setIsLoading(true);

        // If the workstreamId is 0, selected all workstreams
        const url = (selectedWorkstream.workstreamId === 0) ?
            "Reports/StatusReport?" + app.workPackageOptions?.map(wpo => `ids=${wpo.workstreamId}`).join('&')
            : `Reports/StatusReport/${selectedWorkstream.workstreamId}`;

        document.body.style.cursor = 'wait';
        fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((data) => {
                const statusReport: StatusReport = data;
                statusReport.health.workstreamName = data.workstreamName;
                statusReport.health.workstreamId = data.workstreamId;
                setWorkstreamStatusReport(statusReport);

                // check if local storage has a report for the selected workstream
                const storedReport = localStorage.getItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId);
                if (storedReport) {
                    try {
                        const formData = JSON.parse(storedReport);
                        setWorkstreamStatusReport({
                            ...statusReport,
                            activitiesSince: formData.activitiesSince || "",
                            nextSteps: formData.nextSteps || "",
                        });
                        setActivitiesCount(formData.activitiesSince?.length ?? 0);
                        setNextStepsCount(formData.nextSteps?.length ?? 0);
                    } catch (error) {
                        console.error("Error parsing local storage data:", error);
                    }
                }
                else {
                    setActivitiesCount(statusReport.activitiesSince?.length ?? 0);
                    setNextStepsCount(statusReport.nextSteps?.length ?? 0);
                }
                setIsLoading(false);
            })
            .catch(() => {
                app.showActionNotification("Error", "Error loading status report", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function saveSelectedWorkstream(workstream: WorkstreamOption) {
        // Save the selected workstream to local storage
        localStorage.setItem(app.applicationUser?.id + "_SR_SelectedWS_" + app.selectedProject?.id, JSON.stringify(workstream));
        setSelectedWorkstream(workstream);
    }

    function loadSelectedWorkstream(): WorkstreamOption | null {
        // Load the selected workstream from local storage
        const storedWorkstream = localStorage.getItem(app.applicationUser?.id + "_SR_SelectedWS_" + app.selectedProject?.id);
        if (storedWorkstream) {
            try {
                const workstream = JSON.parse(storedWorkstream) as WorkstreamOption;
                // Validate the workstream object
                if (workstream && typeof workstream.workstreamId === 'number' && typeof workstream.workstreamName === 'string') {
                    return workstream;
                } else {
                    console.error("Invalid workstream data in local storage:", workstream);
                    return null; // Return null if the data is invalid
                }
            } catch (error) {
                console.error("Error parsing workstream data from local storage:", error);
                return null; // Return null if parsing fails
            }
        }
        return null; // Return null if no workstream is found
    }

    function saveFormDataLocally() {
        if (!workstreamStatusReport || demoMode) return;
        const formData = {
            activitiesSince: workstreamStatusReport.activitiesSince,
            nextSteps: workstreamStatusReport.nextSteps,
        };
        localStorage.setItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId, JSON.stringify(formData));
        setFormMessage(getFormMessage()); // Update the form message to show unsaved changes
    }

    function discardChanges() {
        localStorage.removeItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId);
        loadStatusReport(); // Reload the status report to reset the form 
        setFormMessage(getFormMessage()); // Update the form message
        app.showActionNotification("Cancelled", "Changes discarded", "default");
    }

    function getFormMessage() {
        if (!workstreamStatusReport) {
            return <div className="my-auto">Status report data not loaded</div>;
        }
        const storedData = localStorage.getItem("SR_FormData_" + app.selectedProject?.id + "_" + selectedWorkstream?.workstreamId);
        if (storedData) {
            return <div className="my-auto flex flex-row">Unsaved changes <button className="ml-2" onClick={() => discardChanges()} ><UndoIcon className="w-4 h-4" /></button></div>
        } else {
            return <div className="my-auto">{(workstreamStatusReport?.statusReportId !== 0 && workstreamStatusReport?.date !== undefined) ? "Last Updated - " + format(new Date(workstreamStatusReport?.date), dateformat) : ""}</div>
        }
    }

    function populateDemoData() {
        const demoWorkPackages: WorkPackageBasic[] = [];
        const demoRiskIssues: RiskIssueBasic[] = [];
        const demoSupportRequests: SupportRequestBasic[] = [];

        // Generate 4 work packages
        for (let i = 0; i < 4; i++) {
            const wpStartDate = new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000); // Random date in last 60 days
            const wpEndDate = new Date(wpStartDate.getTime() + (Math.random() * 90 + 30) * 24 * 60 * 60 * 1000); // 30-120 days after start

            const wpTasks: TaskBasic[] = [];
            // Generate 3-6 tasks per work package
            const taskCount = Math.floor(Math.random() * 4) + 3;
            for (let j = 0; j < taskCount; j++) {
                const isMilestone = Math.random() < 0.3;
                const isDeliverable = !isMilestone && Math.random() < 0.3;
                const displayOnStatusReport = isMilestone || isDeliverable || Math.random() < 0.4;
                const completed = Math.random() < 0.4;
                const taskFinishDate = new Date(wpStartDate.getTime() + Math.random() * (wpEndDate.getTime() - wpStartDate.getTime()));
                const statuses: (2 | 1 | 4)[] = [1, 2, 4];

                wpTasks.push({
                    taskId: i * 100 + j,
                    name: isMilestone ? `Milestone ${i + 1}.${j + 1}` : isDeliverable ? `Deliverable ${i + 1}.${j + 1}` : `Task ${i + 1}.${j + 1}`,
                    status: completed ? 4 : statuses[Math.floor(Math.random() * 2)], // Green if completed, else Red or Amber
                    finishDate: taskFinishDate,
                    completed: completed,
                    completedDate: completed ? new Date(taskFinishDate.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000) : new Date(),
                    isMilestone: isMilestone,
                    isDeliverable: isDeliverable,
                    displayOnCharter: Math.random() < 0.5,
                    displayOnStatusReport: displayOnStatusReport
                });
            }

            demoWorkPackages.push({
                workPackageId: i + 1,
                name: `Work Package ${i + 1}`,
                startDate: wpStartDate,
                finishDate: wpEndDate,
                tasks: wpTasks
            });
        }

        // Generate 3-5 risks/issues
        const riskIssueCount = Math.floor(Math.random() * 3) + 3;
        const ratings: ("High" | "Medium" | "Low")[] = ["High", "Medium", "Low"];
        const riskDescriptions = [
            "Resource availability may impact schedule",
            "Third-party dependency not yet confirmed",
            "Budget variance tracking required",
            "Technical complexity higher than estimated",
            "Stakeholder approval pending"
        ];
        const mitigations = [
            "Weekly resource planning meetings scheduled",
            "Alternative vendors being evaluated",
            "Monthly budget reviews implemented",
            "Technical spike completed to reduce uncertainty",
            "Executive steering committee meeting scheduled"
        ];

        for (let i = 0; i < riskIssueCount; i++) {
            demoRiskIssues.push({
                id: i + 1,
                description: riskDescriptions[i % riskDescriptions.length],
                mitigation: mitigations[i % mitigations.length],
                rating: ratings[Math.floor(Math.random() * 3)],
                isRisk: Math.random() < 0.6 // 60% risks, 40% issues
            });
        }

        // Generate 2-4 support requests
        const supportRequestCount = Math.floor(Math.random() * 3) + 2;
        const supportDescriptions = [
            "Additional test environment access required",
            "Approval needed for change request CR-2024-001",
            "Clarification on acceptance criteria for deliverable",
            "Resource backfill needed for team member on leave"
        ];

        for (let i = 0; i < supportRequestCount; i++) {
            demoSupportRequests.push({
                supportRequestId: i + 1,
                description: supportDescriptions[i % supportDescriptions.length],
                date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000) // Random date in last 30 days
            });
        }

        const demoReport: StatusReport = {
            statusReportId: 999,
            workstreamId: selectedWorkstream?.workstreamId || 1,
            workstreamName: selectedWorkstream?.workstreamName || "Demo Workstream",
            date: new Date(),
            estimatedCloseDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
            health: {
                workstreamId: selectedWorkstream?.workstreamId || 1,
                overall: 2, // Amber
                scope: 1,   // Red
                schedule: 2, // Amber
                resources: 4,  // Green
                financial: 2,  // Amber
                other: 2
            },
            activitiesSince: "• Completed design review with stakeholders\n• Finalized technical architecture\n• Onboarded two new team members\n• Resolved 12 outstanding issues",
            nextSteps: "• Begin development sprint 3\n• Schedule integration testing\n• Prepare for UAT phase\n• Submit change request for scope adjustment",
            workPackages: demoWorkPackages,
            riskIssues: demoRiskIssues,
            supportRequests: demoSupportRequests
        };
        setDemoStatusReport(demoReport);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Status Report page without making any actual changes to the data.
                        </p>
                        <p>
                            The report will be populated with sample data including work packages, tasks, risks, issues and support requests.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        demoMode,
        setDemoMode,
        demoStatusReport,
        setDemoStatusReport,
    };

    if (isLoading) {
        return <Loading />
    }

    if (isMobile) {
        return (
            <div className="flex flex-col w-full p-2 space-y-2 mb-8 relative" style={{ zoom: zoomLevel }}>
                {(app.authLevel > 3) && <div className="flex flex-row align-center text-xl absolute top-0 right-0 -translate-y-full">{lock.getLockControl()}<div className="my-auto" onClick={loadStatusReport}>{lock.getRefreshControl()}</div><HelpLink page="status_report" />{demoMode && demoButton}</div>}
                <div className="flex flex-row w-full justify-between p-2">
                    <div className="flex flex-row space-x-2">
                        {app.selectedProject?.projectTypeId === 2 ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="bg-sidebar-background border-none text-lg shadow-none">
                                        {selectedWorkstream?.workstreamName ?? (app.workPackageOptions?.[0] as WorkPackageOptions)?.workstreamName ?? "No Workstreams"}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {app.workPackageOptions && app.workPackageOptions.map((workstream) => (
                                        <DropdownMenuItem key={workstream.workstreamId} onClick={() => saveSelectedWorkstream({ workstreamId: workstream.workstreamId, workstreamName: workstream.workstreamName })}>
                                            {workstream.workstreamName}
                                        </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuItem onClick={() => saveSelectedWorkstream({ workstreamId: 0, workstreamName: app.selectedProject?.name ?? "Project" })}>
                                        {app.selectedProject?.name ?? "Project"}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : <div className="text-lg font-semibold">{app.selectedProject?.name ?? "Project"}</div>}
                        <div className="text-lg font-semibold">Status Report</div>
                    </div>
                    <div className="flex flex-row space-x-2 text-xl text-right">
                        <div>{date}</div>
                        <div>{time}</div>
                    </div>
                </div>

                {displayReport ? (
                    <div className="w-full h-[400px]">
                        <StatusReportChart
                            estimatedCloseDate={displayReport?.estimatedCloseDate}
                            health={displayReport?.health}
                        />
                    </div>
                ) : (<Card className="flex flex-col justify-center p-4 w-full h-[400px]"></Card>)}

                <Card className="flex flex-col w-full p-2 max-h-[400px]">
                    <div className="h-full w-full overflow-x-auto overflow-y-auto">
                        <Table className="table-auto w-full text-table-text bg-table-body rounded-md">
                            <TableHeader className="bg-table-header text-table-header-foreground text-center sticky top-0 z-10">
                                <TableRow>
                                    <TableHead className="rounded-tl-md p-1 text-xs"></TableHead>
                                    <TableHead className="p-1 text-xl">Key Events</TableHead>
                                    <TableHead className="text-center p-1 text-xl">Finish Date</TableHead>
                                    <TableHead className="text-center rounded-tr-md p-1 text-xl">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="text-xl">
                                {displayReport?.workPackages.filter((wp: WorkPackageBasic) => wp.tasks.some((t: TaskBasic) => t.displayOnStatusReport)).map((pkg: WorkPackageBasic) => (
                                    <React.Fragment key={pkg.workPackageId}>
                                        <TableRow className="bg-table-subheader text-table-subheader-foreground">
                                            <TableCell className="p-1" colSpan={4}>{pkg.name}</TableCell>
                                        </TableRow>
                                        {pkg.tasks.filter(t => t.displayOnStatusReport).map((milestone: TaskBasic) => (
                                            <TableRow key={milestone.taskId} onClick={() => navigate('/Plan', { state: { passedTaskId: milestone.taskId } })}>
                                                <TableCell className="p-1 text-center">{milestone.completed ? <Check className="w-7 h-7 text-chart-green" /> : milestone.isMilestone ? <MilestoneIcon className="w-7 h-7" /> : milestone.isDeliverable ? <DeliverableIcon className="w-7 h-7" /> : <TaskIcon className="w-7 h-7" />}</TableCell>
                                                <TableCell className="p-1">{milestone.name}</TableCell>
                                                <TableCell className="p-1 text-center">{formatDate(new Date(milestone.finishDate), dateformat)}</TableCell>
                                                <TableCell className="p-1 text-center"><TrafficLightComponent TrafficLightColour={milestone.status} Size={7} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>

                <Card className="flex flex-col w-full p-2 max-h-[400px]">
                    <div className="h-full w-full overflow-x-auto overflow-y-auto">
                        <Table className="table-auto w-full text-table-text bg-table-body rounded-md text-xl">
                            <TableHeader className="bg-table-header text-table-header-foreground sticky top-0 z-10">
                                <TableRow><TableHead className="text-center" colSpan={2}>Risks, Issues & Support Requests</TableHead></TableRow>
                                <TableRow>
                                    <TableHead>Description</TableHead>
                                    <TableHead className="text-center p-1">Rating</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayReport?.riskIssues.length && displayReport?.riskIssues.length > 0 ?
                                    displayReport?.riskIssues
                                        .sort((a, b) => {
                                            const ratingOrder = { "High": 1, "Medium": 2, "Low": 3 };
                                            return ratingOrder[a.rating] - ratingOrder[b.rating];
                                        })
                                        .map((riskIssue: RiskIssueBasic) => (
                                            <TableRow key={riskIssue.isRisk ? "r" + riskIssue.id : "i" + riskIssue.id} onClick={() => navigate(riskIssue.isRisk ? '/Risks' : '/Issues', { state: [{ id: riskIssue.isRisk ? "riskId" : "issueId", value: riskIssue.id }] })}>
                                                <TableCell className="p-1">{riskIssue.description}</TableCell>
                                                <TableCell className="p-1 text-center"><TrafficLightComponent TrafficLightColour={riskIssue.rating === "High" ? 1 : riskIssue.rating === "Medium" ? 2 : 4} Size={4} /></TableCell>
                                            </TableRow>
                                        )) : <TableRow><TableCell className="p-1" colSpan={2}>No Risks or Issues</TableCell></TableRow>}
                            </TableBody>
                            <TableHeader className="bg-table-header text-table-header-foreground">
                                <TableRow>
                                    <TableHead>Support Request</TableHead>
                                    <TableHead className="text-center p-1">Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayReport?.supportRequests && displayReport?.supportRequests.length > 0 ?
                                    displayReport?.supportRequests.map((supportRequest: SupportRequestBasic) => (
                                        <TableRow key={supportRequest.supportRequestId} onClick={() => navigate('/SupportRequests', { state: [{ id: "supportRequestId", value: supportRequest.supportRequestId }] })}>
                                            <TableCell className="p-1">{supportRequest.description}</TableCell>
                                            <TableCell className="text-center p-1">{formatDate(new Date(supportRequest.date), dateformat)}</TableCell>
                                        </TableRow>
                                    )) : <TableRow><TableCell className="p-1" colSpan={2}>No Support Requests</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                </Card>

                <Card className="flex flex-col w-full p-2">
                    <div className="flex flex-col w-full text-table-text bg-table-body rounded-md">
                        <div className="flex flex-col">
                            <div className="flex flex-row justify-between px-2 bg-table-header text-table-header-foreground rounded-t-md sticky top-0">
                                <div className="p-2 text-xl">Activities Since Last Meeting</div>
                                <span className="text-xs flex items-end pr-2">{activitiessCount}/750</span>
                            </div>
                            <Textarea
                                value={displayReport?.activitiesSince || ""}
                                onChange={(e) => {
                                    const value = e.currentTarget.value;
                                    setActivitiesCount(value.length);
                                    if (demoMode && demoStatusReport) {
                                        setDemoStatusReport({ ...demoStatusReport, activitiesSince: value });
                                        return;
                                    }
                                    if (workstreamStatusReport) {
                                        setWorkstreamStatusReport({ ...workstreamStatusReport, activitiesSince: value });
                                    }
                                }}
                                onBlur={() => saveFormDataLocally()}
                                placeholder="Enter activities..."
                                className="resize-none bg-table-body text-table-text text-xl"
                                rows={5}
                                maxLength={750}
                                disabled={app.authLevel < 4 || lock.viewOnly}
                            />
                        </div>

                        <div className="flex flex-col rounded-b-md">
                            <div className="flex flex-row justify-between px-2 bg-table-header text-table-header-foreground">
                                <div className="p-2 text-xl">Next Steps</div>
                                <span className="text-xs flex items-end pr-2">{nextStepsCount}/750</span>
                            </div>
                            <Textarea
                                placeholder="Enter next steps..."
                                className="resize-none bg-table-body text-table-text text-xl"
                                rows={5}
                                maxLength={750}
                                disabled={app.authLevel < 4 || lock.viewOnly}
                                value={displayReport?.nextSteps || ""}
                                onChange={(e) => {
                                    const value = e.currentTarget.value;
                                    setNextStepsCount(value.length);
                                    if (demoMode && demoStatusReport) {
                                        setDemoStatusReport({ ...demoStatusReport, nextSteps: value });
                                        return;
                                    }
                                    if (workstreamStatusReport) {
                                        setWorkstreamStatusReport({ ...workstreamStatusReport, nextSteps: value });
                                    }
                                }}
                                onBlur={() => saveFormDataLocally()}
                            />
                        </div>
                    </div>
                </Card>

                <div className="flex flex-row gap-2 pb-4 mb-4">
                    <button className="flex-1 button-default"
                        disabled={workstreamStatusReport?.statusReportId === 0 || app.authLevel < 4 || lock.viewOnly}
                        onClick={() => UpdateReport()}
                    >
                        Update Report
                    </button>
                    <button className="flex-1 button-default"
                        disabled={app.authLevel < 4 || lock.viewOnly}
                        onClick={() => CreateReport()}
                    >
                        Create New
                    </button>
                </div>
            </div>
        );
    }

    return (
        <StatusReportContext.Provider value={contextValue}>
            {wizard && wizard.getWizardPointer()}
            <div className="flex flex-col items-center w-full h-screen max-h-[calc(100vh-64px)] overflow-hidden" style={{ zoom: zoomLevel }}>
                <div className="flex flex-row w-full p-2 place-content-between w-full glassmorphism-card">
                    <div className="flex flex-row">
                        {app.selectedProject?.projectTypeId === 2 ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button id="workstream-dropdown" variant="outline" size="icon" className="bg-sidebar-background border-none text-2xl w-full shadow-none mx-auto pl-2 mr-2">
                                        {selectedWorkstream?.workstreamName ?? (app.workPackageOptions?.[0] as WorkPackageOptions)?.workstreamName ?? "No Workstreams"}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="center">
                                    {app.workPackageOptions && app.workPackageOptions.map((workstream) => (
                                        <DropdownMenuItem key={workstream.workstreamId} onClick={() => saveSelectedWorkstream({ workstreamId: workstream.workstreamId, workstreamName: workstream.workstreamName })} className="flex flex-row justify-between">
                                            <div className="flex flex-row max-w-[200px]">{workstream.workstreamName}</div>
                                        </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuItem onClick={() => saveSelectedWorkstream({ workstreamId: 0, workstreamName: app.selectedProject?.name ?? "Project" })} className="flex flex-row justify-between">
                                        <div className="flex flex-row max-w-[200px]">{app.selectedProject?.name ?? "Project"}</div>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : <div className="flex text-2xl mt-[2px] mr-[6px] w-full whitespace-nowrap">{app.selectedProject?.name ?? "Project"}</div>}
                        <div className="flex text-2xl mt-[2px] w-full whitespace-nowrap">Status Report</div>
                    </div>
                    {(app.authLevel > 3) && <div className="flex flex-row justify-between align-center">{lock.getLockControl()}<div className="my-auto" onClick={loadStatusReport}>{lock.getRefreshControl()}</div><HelpLink page="status_report" />{demoMode && demoButton}</div>}
                    <div className="flex flex-row justify-self-end space-x-2 pr-2 pt-2">
                        <div className="flex flex-row my-auto">{date} {time}</div>
                        <Button id="update-report-button" className="flex flex-row border-2 border-accent rounded-md p-2 bg-card text-foreground hover:bg-primary/60"
                            disabled={workstreamStatusReport?.statusReportId === 0 || app.authLevel < 4 || lock.viewOnly}
                            onClick={() => {
                                UpdateReport();
                            }}
                        >
                            Update Report <FileSymlink />
                        </Button>
                        <Button id="create-report-button" className="flex flex-row border-2 border-accent rounded-md p-2 bg-card text-foreground hover:bg-primary/60"
                            disabled={app.authLevel < 4 || lock.viewOnly}
                            onClick={() => {
                                CreateReport();
                            }}
                        >Create New Report <FileStack /></Button>
                    </div>
                </div>
                <div className="flex flex-row w-full p-2 space-x-2 h-[45%]">
                    {displayReport ? (
                        <StatusReportChart
                            estimatedCloseDate={displayReport?.estimatedCloseDate}
                            health={displayReport?.health}
                        />
                    ) : (<Card id="status-report-chart" className="flex flex-col justify-center pb-2 w-1/2"></Card>)}
                    <Card id="key-events-table" className="flex flex-col items-center w-1/2 p-2 h-full">
                        <div className="h-full w-full overflow-y-auto scrollbar">
                            <Table className="table-auto w-full text-table-text bg-table-body my-auto rounded-md">
                                <TableHeader className="bg-table-header text-table-header-foreground rounded-tr-md rounded-tl-md text-center">
                                    <TableRow>
                                        <TableHead className="rounded-tl-md p-2"></TableHead>
                                        <TableHead className="p-2"></TableHead>
                                        <TableHead>Key Events</TableHead>
                                        <TableHead className="text-center">Planned Finish Date</TableHead>
                                        <TableHead className="text-center">Status</TableHead>
                                        <TableHead className="text-center rounded-tr-md">Forecast Finish Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="text-(hsl(0,0%,98%))">
                                    {displayReport?.workPackages.filter((wp: WorkPackageBasic) => wp.tasks.some((t: TaskBasic) => t.displayOnStatusReport)).map((pkg: WorkPackageBasic) => (
                                        <>
                                            <TableRow key={pkg.workPackageId} className="bg-table-subheader text-table-subheader-foreground">
                                                <TableCell className="p-1"></TableCell>
                                                <TableCell className="p-1"></TableCell>
                                                <TableCell className="p-1">{pkg.name}</TableCell>
                                                <TableCell className="p-1"></TableCell>
                                                <TableCell className="p-1"></TableCell>
                                                <TableCell className="p-1"></TableCell>
                                            </TableRow>
                                            {pkg.tasks.find(t => t.displayOnStatusReport) ? (pkg.tasks.filter(t => t.displayOnStatusReport).map((milestone: TaskBasic) => (
                                                <TableRow key={milestone.taskId} onClick={() => navigate('/Plan', { state: { passedTaskId: milestone.taskId } })} className="key-events-table-row hover:cursor-pointer">
                                                    <TableCell className="p-1 text-center">{milestone.completed ? <Check className="w-5 h-5 text-chart-green" /> : ""}</TableCell>
                                                    <TableCell className="p-1 text-center">{milestone.isMilestone ? <MilestoneIcon className="w-5 h-5" /> : milestone.isDeliverable ? <DeliverableIcon className="w-5 h-5" /> : <TaskIcon className="w-5 h-5" />}</TableCell>
                                                    <TableCell className="p-1">{milestone.name}</TableCell>
                                                    <TableCell className="p-1 text-center">{formatDate(new Date(milestone.finishDate), dateformat)}</TableCell>
                                                    <TableCell className="p-1"><TrafficLightComponent TrafficLightColour={milestone.status} Size={5} /></TableCell>
                                                    <TableCell className="p-1 text-center">{milestone.completedDate ? formatDate(new Date(milestone.completedDate), dateformat) : "-"}</TableCell>
                                                </TableRow>
                                            ))) :
                                                <TableRow>
                                                    <TableCell className="p-1 text-center" colSpan={5}>No events selected for report</TableCell>
                                                </TableRow>
                                            }
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                </div>
                <div className="flex flex-row items-center w-full h-[47%] p-2 space-x-2">
                    <Card id="risks-issues-support-requests" className="flex flex-col items-center w-1/2 p-2 h-full relative">
                        <div className="h-full w-full overflow-y-auto scrollbar">
                            <Table className="table-auto w-full text-table-text bg-table-body rounded-b-md">
                                <TableHeader className="bg-table-header text-table-header-foreground">
                                    <TableRow><TableHead className="text-center" colSpan={3}>Risks, Issues & Support Requests</TableHead></TableRow>
                                    <TableRow>
                                        <TableHead className="text-center">Risk / Issues</TableHead>
                                        <TableHead className="text-center">Mitigation</TableHead>
                                        <TableHead className="text-center p-2">Rating</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="text-(hsl(0,0%,98%))">
                                    {displayReport?.riskIssues.length && displayReport?.riskIssues.length > 0 ?
                                        displayReport?.riskIssues
                                            .sort((a, b) => {
                                                const ratingOrder = { "High": 1, "Medium": 2, "Low": 3 };
                                                return ratingOrder[a.rating] - ratingOrder[b.rating];
                                            })
                                            .map((riskIssue: RiskIssueBasic) => (
                                                <TableRow key={riskIssue.isRisk ? "r" + riskIssue.id : "i" + riskIssue.id} onClick={() => navigate(riskIssue.isRisk ? '/Risks' : '/Issues', {
                                                    state: [
                                                        { id: riskIssue.isRisk ? "riskId" : "issueId", value: riskIssue.id },
                                                    ]
                                                })} className="risks-issues-table-row hover:cursor-pointer">
                                                    <TableCell className="p-1">{riskIssue.description}</TableCell>
                                                    <TableCell className="p-1">{riskIssue.mitigation}</TableCell>
                                                    <TableCell className="p-1"><TrafficLightComponent TrafficLightColour={riskIssue.rating === "High" ? 1 : riskIssue.rating === "Medium" ? 2 : 4} Size={5} /></TableCell>
                                                </TableRow>
                                            )) : <TableRow key={0}><TableCell className="p-1" colSpan={3}>No Risks or Issues selected for report</TableCell></TableRow>}
                                </TableBody>
                                <TableHeader className="bg-table-header text-table-header-foreground">
                                    <TableRow>
                                        <TableHead colSpan={2}>Request</TableHead>
                                        <TableHead className="w-1/6 text-center p-2">Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayReport?.supportRequests && displayReport?.supportRequests.length > 0 ?
                                        displayReport?.supportRequests.map((supportRequest: SupportRequestBasic) => (
                                            <TableRow key={supportRequest.supportRequestId} onClick={() => navigate('/SupportRequests', {
                                                state: [
                                                    { id: "supportRequestId", value: supportRequest.supportRequestId },
                                                ]
                                            })} className="support-requests-table-row hover:cursor-pointer">
                                                <TableCell colSpan={2} className="p-1">{supportRequest.description}</TableCell>
                                                <TableCell className="text-center p-1">{formatDate(new Date(supportRequest.date), dateformat)}</TableCell>
                                            </TableRow>
                                        )) : <TableRow key={0}><TableCell className="p-1" colSpan={2}>No Support Requests selected for report</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        </div>
                        {wizard.getTourControls()}
                    </Card>
                    <Card id="activities-next-steps" className="flex flex-col items-center w-1/2 p-2 h-full">
                        <div className="flex flex-col w-full h-full text-table-text bg-table-body rounded-md">
                            <div className="flex flex-col h-[50%]">
                                <div className="flex flex-row justify-between px-2 bg-table-header text-table-header-foreground rounded-t-md">
                                    <div className="p-2">Activities Since Last Meeting</div>
                                    {FormMessage}
                                    <span className="text-xs text-gray-50 flex items-end pr-2">{activitiessCount}/750</span>
                                </div>

                                <Textarea
                                    id="activities-since-textarea"
                                    value={displayReport?.activitiesSince || ""}
                                    onChange={(e) => {
                                        const value = e.currentTarget.value;
                                        setActivitiesCount(value.length);
                                        if (demoMode && demoStatusReport) {
                                            setDemoStatusReport({ ...demoStatusReport, activitiesSince: value });
                                            return;
                                        }
                                        if (workstreamStatusReport) {
                                            setWorkstreamStatusReport({ ...workstreamStatusReport, activitiesSince: value });
                                        }
                                    }}
                                    onBlur={() => {
                                        saveFormDataLocally(); // Save the form data locally when the field loses focus
                                    }}
                                    placeholder="Please enter activities since the last meeting..."
                                    className="resize-none bg-table-body mb-2 text-table-text"
                                    rows={7}
                                    maxLength={750}
                                    disabled={app.authLevel < 4 || lock.viewOnly}
                                />
                            </div>

                            <div className="flex flex-col rounded-b-md">
                                <div className="flex flex-row justify-between px-2 bg-table-header text-table-header-foreground">
                                    <div className="p-2">Next Steps</div>
                                    {/* <div>Last Updated - {format(new Date(), dateformat)}</div> */}
                                    <span className="text-xs text-gray-50 flex items-end pr-2">{nextStepsCount}/750</span>
                                </div>

                                <Textarea
                                    id="next-steps-textarea"
                                    placeholder="Please enter the next steps..."
                                    className="resize-none bg-table-body text-table-text"
                                    rows={7}
                                    //{...field}
                                    maxLength={750}
                                    disabled={app.authLevel < 4 || lock.viewOnly}
                                    value={displayReport?.nextSteps || ""}
                                    onChange={(e) => {
                                        const value = e.currentTarget.value;
                                        setNextStepsCount(value.length);
                                        if (demoMode && demoStatusReport) {
                                            setDemoStatusReport({ ...demoStatusReport, nextSteps: value });
                                            return;
                                        }
                                        if (workstreamStatusReport) {
                                            setWorkstreamStatusReport({ ...workstreamStatusReport, nextSteps: value });
                                        }
                                    }}
                                    onBlur={() => {
                                        saveFormDataLocally(); // Save the form data locally when the field loses focus
                                    }}
                                />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
            <TutorialChecker
                tutorialId={Tutorial.StatusReport}
                tutorial={async () => {
                    setDemoMode(true);
                    const acquiredLock = await lock.lockPage();
                    wizard.setWizardSteps(getBasicTourSteps);
                    wizard.setGuidedTourCompleteFunctionCall(() => () => { lock.unlockPage(acquiredLock?.id); setDemoMode(false); });
                    wizard.startGuidedTour();
                }} />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent id="risk-dialog" className="flex flex-col max-w-fit">
                    <DialogHeader>
                        <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div></div></DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog>
        </StatusReportContext.Provider>
    )
}

function StatusReport() {
    const app = React.useContext(AppContext);

    return <PageLockProvider pageName={PageName.StatusReport} projectId={app.selectedProject?.id as number} userId={app.applicationUser?.id as string}>
        <WizardProvider wizardSteps={getBasicTourSteps} >
            <StatusReportPage />
        </WizardProvider>
    </PageLockProvider>
}

export default StatusReport